<script setup lang="ts">
import TheAppleSigninButton from '@/components/BaseComponents/TheAppleSigninButton.vue';
import TheGoogleSigninButton from '@/components/BaseComponents/TheGoogleSigninButton.vue';
import InlineLink from '@/components/InlineLink.vue';
import InlineMessage from '@/components/InlineMessage.vue';
import { useAppleLogin } from '@/composables/useAppleLogin';
import { useAuth } from '@/composables/useAuth.js';
import { useGoogleLogin } from '@/composables/useGoogleLogin';
import { usePlatform } from '@/composables/usePlatform';
import { type MessageSchema } from '@/i18n';
import { useAppStore } from '@/stores/appStore';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Divider from 'primevue/divider';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { register, loginWithGoogle, loginWithApple, errors, isFetching } = useAuth();
const toast = useToast();
const appStore = useAppStore();
const { isIOS } = usePlatform();

errors.value = {};

const password = ref('');
const passwordConfirmation = ref('');

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const userForm = ref({
  email: '',
  password: '',
  password_confirmation: '',
  first_name: '',
  last_name: '',
  language: appStore.language || 'en',
});

function hasError(fieldName: string) {
  return !!errors.value[fieldName] && errors.value[fieldName].length > 0;
}

const touched = reactive({
  email: false,
  password: false,
  passwordConfirmation: false,
  first_name: false,
  last_name: false,
});

const isEmailValid = computed(() => {
  if (!touched.email) return true;
  return touched.email && userForm.value.email && isValidEmail(userForm.value.email);
});

const isPasswordValid = computed(() => {
  if (!touched.password) return true;
  return touched.password && password.value?.length >= 8;
});

const isPasswordConfirmationValid = computed(() => {
  if (!touched.passwordConfirmation) return true;
  return touched.passwordConfirmation && password.value === passwordConfirmation.value;
});

const isFirstNameValid = computed(() => {
  if (!touched.first_name) return true;
  return touched.first_name && userForm.value.first_name;
});

const isLastNameValid = computed(() => {
  if (!touched.last_name) return true;
  return touched.last_name && userForm.value.last_name;
});

const isFormValid = computed(() => {
  return (
    userForm.value.email &&
    isValidEmail(userForm.value.email) &&
    password.value?.length >= 8 &&
    password.value === passwordConfirmation.value &&
    userForm.value.first_name &&
    userForm.value.last_name
  );
});

const acceptedCGU = ref(false);

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isRegisterDisabled = computed(() => {
  return isFetching.value || !acceptedCGU.value || !isFormValid.value;
});

function openStepAccordingToErrors() {
  Object.keys(touched).forEach((key) => (touched[key] = true));
}

async function submitForm() {
  if (isFormValid.value) {
    await register(userForm.value, password.value, passwordConfirmation.value, toast);
    if (Object.keys(errors.value).length > 0) {
      openStepAccordingToErrors();
    }
  } else {
    openStepAccordingToErrors();
  }
}

const languagesList = ref<{ label: string; value: 'fr' | 'en' | 'de' | 'it' }[]>([
  { label: 'Français', value: 'fr' },
  { label: 'English', value: 'en' },
  { label: 'Deutsch', value: 'de' },
  { label: 'Italiano', value: 'it' },
]);

const selectedLanguage = ref(languagesList.value.find((language) => language.value === appStore.language) || languagesList.value[0]);

function updateLanguage() {
  const newLanguage = selectedLanguage.value.value;
  appStore.setLanguage(newLanguage);
}

onMounted(() => {
  const browserLanguage = navigator.language || navigator.languages[0];
  // Extract only the first two letters (e.g., 'en-US' -> 'en')
  const languageCode = browserLanguage.split('-')[0];
  // Check if your app supports the detected language
  const supportedLanguages = ['en', 'fr', 'de', 'it'];
  if (supportedLanguages.includes(languageCode)) {
    appStore.setLanguage(languageCode as 'en' | 'fr' | 'de' | 'it');
    selectedLanguage.value = languagesList.value.find((language) => language.value === languageCode) || languagesList.value[0];
  }
});

const { login: googleLogin, isLoggingIn: isGoogleLoggingIn } = useGoogleLogin();
const { login: appleLogin, isLoggingIn: isAppleLoggingIn } = useAppleLogin();

const handleGoogleLogin = async () => {
  try {
    const result = await googleLogin();
    // Pass the result with credential structure similar to vue3-google-login
    await loginWithGoogle({ credential: result.idToken });
  } catch (error) {
    console.error('[RegisterView] Google login failed:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('auth.registration.error'),
      life: 5000,
    });
  }
};

const handleAppleLogin = async () => {
  try {
    console.log('[RegisterView] Starting Apple registration process...');
    const result = await appleLogin();
    console.log('[RegisterView] Apple login returned result, passing to loginWithApple...');
    // Same flow as login - backend handles user creation if needed
    await loginWithApple(result);
    console.log('[RegisterView] Apple registration completed successfully');
  } catch (error) {
    console.error('[RegisterView] Apple registration failed:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: error instanceof Error ? error.message : t('auth.registration.error'),
      life: 5000,
    });
  }
};
</script>

<template>
  <main>
    <div class="mb-4 flex justify-end">
      <Dropdown v-model="selectedLanguage" class="w-40" :options="languagesList" option-label="label" @change="updateLanguage">
        <template #value="slotProps">
          {{ slotProps.value.label }}
        </template>
        <template #option="slotProps">
          {{ slotProps.option.label }}
        </template>
      </Dropdown>
    </div>

    <div class="flex flex-col items-center gap-4 align-middle">
      <TheGoogleSigninButton type="sign-up" :loading="isGoogleLoggingIn" :disabled="isGoogleLoggingIn || isFetching" @click="handleGoogleLogin" />

      <TheAppleSigninButton
        v-if="isIOS"
        type="sign-up"
        :loading="isAppleLoggingIn"
        :disabled="isAppleLoggingIn || isFetching"
        @click="handleAppleLogin"
      />
    </div>

    <div class="relative my-4 flex items-center">
      <div class="flex-grow border-t border-gray-300"></div>
      <span class="mx-4 flex-shrink text-gray-600">{{ t('common.or') }}</span>
      <div class="flex-grow border-t border-gray-300"></div>
    </div>

    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <label for="email">{{ t('auth.user.form.label.mail') }} *</label>
        <div>
          <InputText
            id="email"
            v-model="userForm.email"
            type="email"
            class="w-full"
            :placeholder="t('auth.user.form.placeholder.mail')"
            :invalid="hasError('email') || !isEmailValid"
            @blur="touched.email = true"
          />
        </div>
        <InlineMessage v-if="hasError('email') || !isEmailValid" severity="error">
          {{ !isEmailValid ? t('auth.user.form.error.invalid_email') : errors.email[0] }}
        </InlineMessage>
      </div>
      <div class="flex flex-col gap-2">
        <label for="firstName">{{ t('auth.user.form.label.first_name') }} *</label>
        <div>
          <InputText
            id="firstName"
            v-model="userForm.first_name"
            type="text"
            class="w-full"
            :placeholder="t('auth.user.form.placeholder.first_name')"
            :invalid="hasError('first_name') || !isFirstNameValid"
            @blur="touched.first_name = true"
          />
        </div>
        <InlineMessage v-if="hasError('first_name') || !isFirstNameValid" severity="error">
          {{ !isFirstNameValid ? t('auth.user.form.error.invalid_first_name') : errors.first_name[0] }}
        </InlineMessage>
      </div>
      <div class="flex flex-col gap-2">
        <label for="lastName">{{ t('auth.user.form.label.last_name') }} *</label>
        <div>
          <InputText
            id="lastName"
            v-model="userForm.last_name"
            type="text"
            class="w-full"
            :placeholder="t('auth.user.form.placeholder.last_name')"
            :invalid="hasError('last_name') || !isLastNameValid"
            @blur="touched.last_name = true"
          />
        </div>
        <InlineMessage v-if="hasError('last_name') || !isLastNameValid" severity="error">
          {{ !isLastNameValid ? t('auth.user.form.error.invalid_last_name') : errors.last_name[0] }}
        </InlineMessage>
      </div>

      <div class="flex flex-col gap-2">
        <label for="password">{{ t('auth.user.form.label.password') }} *</label>
        <div>
          <Password
            id="password"
            v-model="password"
            :prompt-label="t('auth.user.form.placeholder.password')"
            :weak-label="t('auth.user.form.label.password_weak')"
            :medium-label="t('auth.user.form.label.password_medium')"
            :strong-label="t('auth.user.form.label.password_strong')"
            :invalid="hasError('password') || !isPasswordValid"
            :placeholder="t('auth.user.form.placeholder.password')"
            toggle-mask
            class="w-full"
            aria-describedby="password-help"
            @blur="touched.password = true"
          />
          <small id="password">{{ t('auth.user.form.label.password_help') }}</small>
        </div>
        <InlineMessage v-if="hasError('password') || !isPasswordValid" severity="error">
          {{ !isPasswordValid ? t('auth.user.form.error.invalid_password') : errors.password[0] }}
        </InlineMessage>
      </div>

      <div class="flex flex-col gap-2">
        <label for="passwordConfirmation"> {{ t('auth.user.form.label.password_confirmation') }} * </label>
        <div>
          <Password
            id="passwordConfirmation"
            v-model="passwordConfirmation"
            :prompt-label="t('auth.user.form.placeholder.password')"
            :weak-label="t('auth.user.form.label.password_weak')"
            :medium-label="t('auth.user.form.label.password_medium')"
            :strong-label="t('auth.user.form.label.password_strong')"
            :invalid="hasError('password') || !isPasswordConfirmationValid"
            :placeholder="t('auth.user.form.placeholder.password_confirmation')"
            toggle-mask
            class="w-full"
            aria-describedby="password-help"
            @blur="touched.passwordConfirmation = true"
          />
          <small id="password">{{ t('auth.user.form.label.password_confirmation_help') }}</small>
        </div>
        <InlineMessage v-if="hasError('password') || !isPasswordConfirmationValid" severity="error">
          {{ !isPasswordConfirmationValid ? t('auth.user.form.error.invalid_password_confirmation') : errors.password[0] }}
        </InlineMessage>
      </div>
    </div>
    <div class="mt-8 flex items-center gap-2">
      <Checkbox v-model="acceptedCGU" :binary="true" input-id="acceptCGU" />
      <label for="acceptCGU">
        {{ t('auth.registration.acceptCGU') }}
        <InlineLink :destination="`/conditions-generales/${appStore.language}`" target="_blank">
          {{ t('auth.registration.readCGU') }}
        </InlineLink>
      </label>
    </div>
    <Button
      :label="t('auth.registration.register')"
      :disabled="isRegisterDisabled"
      :loading="isFetching"
      class="mt-4 w-full"
      size="large"
      type="submit"
      @click="submitForm"
    />
    <!-- </form> -->
    <Divider />
    <p>
      {{ t('auth.registration.alreadyHaveAccount') }}
      <InlineLink destination="/login">{{ t('auth.login.login') }} </InlineLink>
    </p>
  </main>
</template>
